import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, ShoppingBag, Package, Users, AlertTriangle, 
  DollarSign, Clock, CheckCircle, XCircle, Truck, Star,
  ArrowUpRight, ArrowDownRight, MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      packed: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      out_for_delivery: 'bg-cyan-100 text-cyan-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-light text-charcoal">Dashboard</h1>
        <p className="text-charcoal-light text-sm mt-1">Welcome back! Here's what's happening with your store.</p>
      </div>

      {/* Sales Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 border border-charcoal/10" data-testid="total-sales-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-gold" />
            </div>
            <span className="text-xs tracking-wider uppercase text-charcoal-light">Total Sales</span>
          </div>
          <p className="font-heading text-3xl font-light text-charcoal">
            {formatCurrency(stats?.sales?.total || 0)}
          </p>
          <p className="text-sm text-charcoal-light mt-2">All time revenue</p>
        </div>

        <div className="bg-white p-6 border border-charcoal/10" data-testid="today-sales-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-50 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-xs tracking-wider uppercase text-charcoal-light">Today</span>
          </div>
          <p className="font-heading text-3xl font-light text-charcoal">
            {formatCurrency(stats?.sales?.today || 0)}
          </p>
          <p className="text-sm text-charcoal-light mt-2">{stats?.orders?.today || 0} orders today</p>
        </div>

        <div className="bg-white p-6 border border-charcoal/10" data-testid="monthly-sales-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-50 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xs tracking-wider uppercase text-charcoal-light">This Month</span>
          </div>
          <p className="font-heading text-3xl font-light text-charcoal">
            {formatCurrency(stats?.sales?.monthly || 0)}
          </p>
          <p className="text-sm text-charcoal-light mt-2">Monthly revenue</p>
        </div>
      </div>

      {/* Order Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-5 border border-charcoal/10 text-center">
          <Package className="w-6 h-6 mx-auto mb-3 text-charcoal-light" />
          <p className="font-heading text-2xl font-light text-charcoal">{stats?.orders?.total || 0}</p>
          <p className="text-xs text-charcoal-light mt-1">Total Orders</p>
        </div>
        <div className="bg-yellow-50 p-5 border border-yellow-200 text-center">
          <Clock className="w-6 h-6 mx-auto mb-3 text-yellow-600" />
          <p className="font-heading text-2xl font-light text-yellow-700">{stats?.orders?.pending || 0}</p>
          <p className="text-xs text-yellow-600 mt-1">Pending</p>
        </div>
        <div className="bg-blue-50 p-5 border border-blue-200 text-center">
          <Truck className="w-6 h-6 mx-auto mb-3 text-blue-600" />
          <p className="font-heading text-2xl font-light text-blue-700">{stats?.orders?.shipped || 0}</p>
          <p className="text-xs text-blue-600 mt-1">In Transit</p>
        </div>
        <div className="bg-green-50 p-5 border border-green-200 text-center">
          <CheckCircle className="w-6 h-6 mx-auto mb-3 text-green-600" />
          <p className="font-heading text-2xl font-light text-green-700">{stats?.orders?.delivered || 0}</p>
          <p className="text-xs text-green-600 mt-1">Delivered</p>
        </div>
        <div className="bg-red-50 p-5 border border-red-200 text-center">
          <XCircle className="w-6 h-6 mx-auto mb-3 text-red-600" />
          <p className="font-heading text-2xl font-light text-red-700">{stats?.orders?.cancelled || 0}</p>
          <p className="text-xs text-red-600 mt-1">Cancelled</p>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 border border-charcoal/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-charcoal flex items-center justify-center">
              <Users className="w-6 h-6 text-ivory" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Customers</p>
              <p className="font-heading text-2xl font-light text-charcoal">{stats?.customers?.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 border border-charcoal/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-charcoal flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-ivory" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Products</p>
              <p className="font-heading text-2xl font-light text-charcoal">{stats?.products?.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 border border-charcoal/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gold flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Pending Reviews</p>
              <p className="font-heading text-2xl font-light text-charcoal">{stats?.pending_reviews || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Low Stock Alert */}
        <div className="bg-white border border-charcoal/10">
          <div className="p-5 border-b border-charcoal/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <h2 className="font-medium text-charcoal">Low Stock Alerts</h2>
            </div>
            <Link 
              to="/admin/products?filter=low_stock"
              className="text-xs tracking-wider uppercase text-gold hover:text-gold-light transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="divide-y divide-charcoal/5">
            {stats?.low_stock_products?.length > 0 ? (
              stats.low_stock_products.map((product) => (
                <div key={product.id} className="p-4 flex items-center gap-4">
                  <img 
                    src={product.image || 'https://via.placeholder.com/60'}
                    alt={product.name}
                    className="w-12 h-12 object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal truncate">{product.name}</p>
                    <p className="text-xs text-red-600 font-medium">Only {product.stock} left</p>
                  </div>
                  <Link 
                    to={`/admin/products?edit=${product.id}`}
                    className="text-xs text-gold hover:text-gold-light"
                  >
                    Edit
                  </Link>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-charcoal-light">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p>All products well stocked!</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Selling Products */}
        <div className="bg-white border border-charcoal/10">
          <div className="p-5 border-b border-charcoal/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <h2 className="font-medium text-charcoal">Top Selling Products</h2>
            </div>
            <Link 
              to="/admin/products"
              className="text-xs tracking-wider uppercase text-gold hover:text-gold-light transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="divide-y divide-charcoal/5">
            {stats?.top_products?.length > 0 ? (
              stats.top_products.map((product, index) => (
                <div key={product.id} className="p-4 flex items-center gap-4">
                  <span className="w-6 h-6 bg-gold/10 flex items-center justify-center text-xs font-medium text-gold">
                    {index + 1}
                  </span>
                  <img 
                    src={product.images?.[0] || 'https://via.placeholder.com/60'}
                    alt={product.name}
                    className="w-12 h-12 object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal truncate">{product.name}</p>
                    <p className="text-xs text-charcoal-light">{product.sold_count || 0} sold</p>
                  </div>
                  <p className="text-sm font-medium text-charcoal">₹{product.price}</p>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-charcoal-light">
                <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No sales data yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-5 border-b border-charcoal/10 flex items-center justify-between">
          <h2 className="font-medium text-charcoal">Recent Orders</h2>
          <Link 
            to="/admin/orders"
            className="text-xs tracking-wider uppercase text-gold hover:text-gold-light transition-colors"
          >
            View All Orders
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige/50">
              <tr>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Order</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Customer</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Amount</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Status</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5">
              {stats?.recent_orders?.length > 0 ? (
                stats.recent_orders.slice(0, 5).map((order) => (
                  <tr key={order.id} className="hover:bg-beige/30 transition-colors">
                    <td className="px-5 py-4">
                      <span className="text-sm font-medium text-charcoal">#{order.order_number}</span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-charcoal">{order.customer_name}</p>
                      <p className="text-xs text-charcoal-light">{order.customer_email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm font-medium text-charcoal">{formatCurrency(order.total_amount)}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getStatusColor(order.order_status)}`}>
                        {order.order_status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-charcoal-light">{formatDate(order.created_at)}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-charcoal-light">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
